// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Solfege Question Generator
//
// Utility object for generating scale degree identification questions
// across all such interactives.
//
import type { Skill } from 'shared/definitions/skill-definitions';
import type { UserSkillStore } from 'shared/src/user/userKnowledge/user.skill.store';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import QuestionGenerator from './questionGenerator';
import KeySignature from '../uTheory.Notation/keySignature';
import utils from '../utils';
import Note from '../uTheory.Notation/note';
import type { ScaleDegree } from '../uTheory.Interaction/solfegeDefinitions';
import type { UserData } from '../uTheory.Interaction/interactivePrototype';
import type { WritingSolfegeSkill } from '../uTheory.Interaction/scaleDegreeNoteQuestionGenerator';

export interface SkillWithProgress extends Skill {
  progress: {
    accuracy: number;
    accuracyScore: number;
    isSpeedRequired: boolean;
    numQuestions: number;
    questionScore: number;
    score: number;
    speed: number;
    speedScore: number;
  };
  score: number;
  isRequired: boolean;
  isOptional: boolean;
  isHidden: boolean;
}

export interface SolfegeQuestionObject {
  /* the number of the question, 1-based */
  text: string;
  /* If a tonicization is needed for this question, it will have a key */
  key?: KeySignature;
  /* The degrees of the question -- if multiple degrees per question */
  degrees?: ScaleDegree[];
  /* The notes of the question -- if multiple degrees per question */
  notes?: Note[];
  /* The degree of the question -- if one degree only per question */
  degree?: ScaleDegree;
  /* The note of the question -- if one degree only per question */
  note?: Note;
  mode: ModeName | 'allMinors';
  skills: string;
  writingSolfegeSkill: string;
  type: 'fragment' | 'singleNote';
}

export interface MutliNoteSolfegeQuestionObject extends SolfegeQuestionObject {
  degrees: ScaleDegree[];
  notes: Note[];
  degree: undefined;
  note: undefined;
  type: 'fragment';
}

export interface SolfegeLevel {
  level: number;
  degrees: ScaleDegree[];
  quality: 'Major' | 'minor';
  singleNoteSkill: string | null;
  multiNoteSkill: string | null;
  writingSolfegeSkill: WritingSolfegeSkill;
  maxInterval?: number;
  firstNoteGiven?: boolean;
  multiOnly?: boolean;
  octaveDisplacement: boolean;
  mode: ModeName | 'allMinors';
}

function getRandomKeys(
  quality: 'Major' | 'minor',
  questions: number,
  tonicizeEvery: number | false = 4,
): KeySignature[] {
  const keysNeeded = tonicizeEvery ? Math.ceil(questions / tonicizeEvery) : 1;
  return KeySignature.randomKeys(keysNeeded, quality);
}

export default class SolfegeQuestionGenerator {
  level: number;

  /**
   * Generates questions based on the maximum distance between notes,
   * not just on a collection of scale degrees.
   * Currently only valid for multi-note-only levels!!!
   */
  private generateIntervalBasedLevelQuestions(
    levelIndex: number = this.level,
    numQuestions = 15,
    options: { tonicizeEvery?: number | false; notesPerQuestion?: number } = {},
    staff = false,
  ): SolfegeQuestionObject[] {
    const level = this.levels.find((l) => l.level === levelIndex);
    const notesPerQuestion = options.notesPerQuestion || 4;
    if (!level.maxInterval)
      throw Error(
        'generateIntervalBasedLevelQuestions can only be used with a level that has a max interval',
      );

    // Get the keys we need:
    const keys = getRandomKeys(level.quality, numQuestions, options.tonicizeEvery || false);
    const questionsPerKey = Math.ceil(numQuestions / keys.length);
    const questionLength = level.firstNoteGiven ? notesPerQuestion + 1 : notesPerQuestion;
    let currentQuestion = 1;

    // Map the keys to questions
    return (
      keys
        .map((key) =>
          new Array(questionsPerKey).fill(0, 0, questionsPerKey).map((): SolfegeQuestionObject => {
            const questions = generateFragmentFromDegreesAndLargestInterval(
              level.degrees,
              level.maxInterval,
              key,
              questionLength,
              staff,
            );
            const degrees = questions.map((q) => q.degree);
            const notes = questions.map((q) => q.note);
            const question: SolfegeQuestionObject = {
              text: `${currentQuestion++}`,
              key,
              mode: level.mode,
              degrees,
              notes,
              skills: level.multiNoteSkill,
              writingSolfegeSkill: level.writingSolfegeSkill,
              type: 'fragment',
            };
            return question;
          }),
        )
        // Flatten the questions
        .flat()
    );
  }

  setLevel(level: number) {
    // Levels:
    // 0. 123 (Major)
    // 1. 123 (minor)
    // 2. 12345 (Major)
    // 3. 12345 (minor)
    // 4. 5678 (Major)
    // 5. 56#78 (minor)
    // 6. 56#67#78 (minor)
    // 7. 1-8 (Major)
    // 8. 1-8 (minor)
    // 9. 1-8 w/ octave displacement (Major)
    // 10. 1-8 w/ octave displacement (minor)

    // If level is specified in options, go with that:
    this.level = level;
  }

  /*
   * generates solfege questions, to string format, for use in multiple
   * solfege interactives.
   *
   * returns questions: [
   *      {
   *        key: if different,
   *
   *        // when one-note-per-question:
   *        note: note,
   *        degrees: degree,
   *
   *        // when multiple-notes-per-question:
   *        notes: [ ],
   *        degrees: [ ],
   *      }
   *    ]
   *
   * options = {
   *   tonicizeEvery: int, default false // each 5 questions, for instance
   *   notesPerQuestion: int, default 1 // 3 notes per question, for instance\
   * }
   *
   * @param {boolean} [staff] // Whether this is a staffID question, in which case questions will only be
   * generated from C2 to C6
   *
   */
  generateQuestions(
    level: number = this.level,
    numQuestions = 15,
    options: { tonicizeEvery?: number | false; notesPerQuestion?: number } = {},
    staff = false,
  ): SolfegeQuestionObject[] {
    const levelObj = this.levels.find((l) => l.level === level);
    if (!levelObj) {
      throw Error(`No level object defined for level ${level} in solegeQuestionGenerator`);
    }

    if (levelObj.maxInterval) {
      const questions = this.generateIntervalBasedLevelQuestions(
        level,
        numQuestions,
        options,
        staff,
      );
      return questions;
    }
    const possibleDegrees = levelObj.degrees;

    const notesPerQuestion = options.notesPerQuestion || 1;
    const tonicizeEvery = options.tonicizeEvery || false;
    const keysNeeded = tonicizeEvery ? Math.ceil(numQuestions / tonicizeEvery) : 1;

    const generator = new QuestionGenerator();
    const scaleDegrees: ScaleDegree[] = generator.scaleDegreeHelper(
      numQuestions * notesPerQuestion,
      possibleDegrees,
    );

    // Generate random keys, elminate enharmonically equivalent keys
    const keys = KeySignature.randomKeys(
      keysNeeded, // how many keys?
      levelObj.quality, // what quality?
      true, // omit enharmonics?
    ).map((randomKey) => {
      const clefs = randomKey.scale.prefersClef();
      const treble = clefs.length === 1 && clefs[0] === 'treble';
      if (treble && Math.random() > 0.5) {
        // move key down octaves until it fits in bass clef so tonicization is pleasing
        let emergencyExit = 0;
        while (!randomKey.scale.prefersClef().includes('bass') && emergencyExit <= 3) {
          emergencyExit++;
          const lowerOctave = randomKey.keyNote.clone();
          lowerOctave.octave--;
          randomKey.set(lowerOctave, randomKey.quality);
        }
      }
      return randomKey;
    });

    let key: KeySignature = keys[0];

    // loop through and get some questions; if the set of n
    // we're on matches the prior set, generate new ones.
    const questionObjects: SolfegeQuestionObject[] = [];
    for (let q = 0; q < numQuestions; q++) {
      const qObj: SolfegeQuestionObject = {
        text: `${q + 1}`,
        skills: levelObj.singleNoteSkill,
        writingSolfegeSkill: levelObj.writingSolfegeSkill,
        type: 'singleNote',
        mode: levelObj.mode,
      };

      // Init key if needed:
      if (q === 0 || (tonicizeEvery && q % tonicizeEvery === 0)) {
        key = keys.splice(0, 1)[0];
        qObj.key = key;
        qObj.mode = levelObj.mode;
      }

      if (notesPerQuestion > 1) {
        qObj.type = 'fragment';
        let qDegrees = scaleDegrees.splice(0, notesPerQuestion);
        if (q > 0 && utils.equals(qDegrees, questionObjects[q - 1].degrees)) {
          let emergencyExit = 0;
          do {
            ++emergencyExit;
            qDegrees = generator.scaleDegreeHelper(notesPerQuestion, possibleDegrees);
          } while (emergencyExit < 1000 && utils.equals(qDegrees, questionObjects[q - 1].degrees));
        }
        qObj.degrees = qDegrees;
        qObj.notes = qDegrees.map(degreesToNotes);
        qObj.skills = levelObj.multiNoteSkill;
      } else {
        qObj.type = 'singleNote';
        qObj.degree = scaleDegrees[q];
        qObj.note = key.getNoteAtScaleDegreeOrThrow(qObj.degree);
        qObj.skills = levelObj.singleNoteSkill;
        if (levelObj.octaveDisplacement) {
          qObj.note.octave = Math.floor(Math.random() * 5) + 2;
          // If this is a staffID question, correct the range.
          if (staff) qObj.note.octave = SolfegeQuestionGenerator.staffRangeCorrect(qObj.note);
        }
      }
      questionObjects.push(qObj);
    }

    return questionObjects;

    function degreesToNotes(degree) {
      const note = key.getNoteAtScaleDegreeOrThrow(degree);
      if (levelObj.octaveDisplacement) {
        note.octave = Math.floor(Math.random() * 5) + 2;
        // If this is a staffID question, correct the range.
        if (staff) note.octave = SolfegeQuestionGenerator.staffRangeCorrect(note);
      }
      return note;
    }
  }

  /* @ processManualQuestions:
   *
   * Question format is:
   *  semi-colon delineates questions
   *  comma delineates question parts
   *  o delineates octave of a note, when outside normal key range.
   *
   *   BbM:1,5,4,2,3;6,2,7,5,2;E:3,5,1,7,6
   *  or, more simply: G:1;2;5;6;7;8;3
   *  octaves specified with "o": A:3o5,M6o2;
   *
   * Returns question objects as generateQuestions specs above
   */
  processManualQuestions(questions: string[], level: number) {
    if (!Array.isArray(questions)) {
      throw new Error('one- and multi-note solfege/staff questions must be in array format.');
    }
    let key: KeySignature;
    if (typeof level === 'undefined') {
      throw new Error('A level must be defined, even for manual questions, in scale degree id.');
    }

    const { singleNoteSkill, multiNoteSkill, mode, writingSolfegeSkill } = this.levels.find(
      (l) => l.level === level,
    );
    return questions.map((q: string, index) => {
      q = `${q}`; // cast as string to be safe.
      // remove spaces:
      q = q.replace(/\s+/g, '');

      const qObj: SolfegeQuestionObject = {
        skills: singleNoteSkill,
        writingSolfegeSkill,
        text: `${index + 1}`,
        type: 'singleNote',
        mode,
      };

      let keySplit = q.indexOf(':');
      if (keySplit !== -1) {
        key = new KeySignature(q.slice(0, keySplit));
        q = q.slice(++keySplit);
        qObj.key = key;
      }

      const degrees = q.split(',');
      if (degrees.length === 1) {
        utils.merge(qObj, processDegree(degrees[0]));
        qObj.type = 'singleNote';
      } else {
        qObj.type = 'fragment';
        qObj.skills = multiNoteSkill;
        qObj.notes = [];
        qObj.degrees = [];
        for (let i = 0; i < degrees.length; i++) {
          const degObj = processDegree(degrees[i]);
          qObj.notes.push(degObj.note);
          qObj.degrees.push(degObj.degree);
        }
      }
      return qObj;
    }, this);

    function processDegree(degString: string) {
      const degArray = degString.split('o');
      const degree = degArray[0];
      // degree = utils.isNumeric(degree) ? Number(degree) : degree;
      const note = key.getNoteAtScaleDegreeOrThrow(degree);
      const normalizedDegree = key.getScaleDegreeAtNote(note);
      if (!normalizedDegree) {
        throw Error(`${degString} is not a valid scale degree.`);
      }
      if (degArray[1]) {
        note.octave = Number(degArray[1]);
      }

      return {
        degree: normalizedDegree,
        note,
      };
    }
  }

  /**
   * Returns recommended training level based on user's skills array
   * @param {UserSkillStore[]} skillsWithProgress from API, array of skills progress objects: {
   *   id: skillId, int format
   *   score: int -- total score
   *   progress {
   *     accuracy
   *     accuracyScore
   *     questions
   *     questionsScore
   *     speed
   *     speedScore
   *   }
   * }
   * @param {string} [singleOrMultiple] {string: "single" or "multiple"}
   *   should we examine the skills for sd_ (one-note) or
   *   sd_frag (multiple-note) questions? Default: "single"
   * @returns {number} the integer level.
   */
  getRecommendedLevel(
    userData: UserData,
    singleOrMultiple: 'single' | 'multiple' = 'single',
  ): number {
    const skillsWithProgress = userData.progress;
    const multi = singleOrMultiple === 'multiple';

    const getLevelProgress = (level: SolfegeLevel) => {
      const levelSkill = multi ? level.multiNoteSkill : level.singleNoteSkill;
      const skillWithProgress = skillsWithProgress.find((s) => s.textId === levelSkill);
      const levelProgress = skillWithProgress;
      return levelProgress;
    };

    const levels = utils.clone(this.levels, true);

    function getSkillForLevel(level: SolfegeLevel): UserSkillStore | false {
      const skillId = multi ? level.multiNoteSkill : level.singleNoteSkill;
      const skill = skillsWithProgress.find((s) => s.textId === skillId);
      return skill || false;
    }
    function isVisible(level: SolfegeLevel) {
      const skill = getSkillForLevel(level);
      // console.log(`${skill} is visible? ${skill && skill.isVisible}`);
      return skill && skill.isVisible;
    }

    function isRequired(levelA: SolfegeLevel, levelB: SolfegeLevel) {
      const skillA = getSkillForLevel(levelA);
      const skillB = getSkillForLevel(levelB);
      if (skillA && !skillB) return -1;
      if (skillB && !skillA) return 1;
      if (!skillB || !skillA) return 0;
      if (skillA.isRequired === skillB.isRequired) return 0;
      if (skillA.isRequired) return -1;
      return 1;
    }
    /*
      ... only levels that are visible to the student ...
      ... never more than one past last started level...
      ... never more than two past last incomplete level...
      ... if all but last two completed, alternate last two levels, but
          prefer the one whose score is significantly lower.
    */

    const availableLevels = levels
      .filter(isVisible)
      .sort(isRequired)
      .filter((level) => !(singleOrMultiple === 'single' && level.multiOnly))
      .map((level) => {
        const levelProgress = getLevelProgress(level);
        // console.log(`level: ${level.singleNoteSkill}, score: ${levelProgress?.score}`);
        const started = levelProgress.numQuestions;
        const completed = levelProgress.score === 100;
        const score = levelProgress.score ? levelProgress.score : 0;
        return {
          ...level,
          started,
          completed,
          score,
        };
      })
      .map((level, index, array) => {
        const priorLevelStarted = array.slice(0, index).every((el, i) => i === index || el.started);
        const withinTwoOfLastCompleted = !array[index - 2] || array[index - 2]?.score > 95;
        // console.log(
        //   `Level: ${level.singleNoteSkill},
        //     priorLevelStarted: ${priorLevelStarted},
        //     withinTwoOfLastCompleted: ${withinTwoOfLastCompleted},
        //     levelCompleted: ${level.completed}`,
        // );

        return {
          ...level,
          priorLevelStarted,
          withinTwoOfLastCompleted,
        };
      })
      .filter(
        (level) => level.priorLevelStarted && level.withinTwoOfLastCompleted && !level.completed,
      );

    // console.log({ availableLevels });

    // If there are multiple levels available, randomize them.
    // If there aren't, then they're at the top level, pick the
    // lower scoring one.
    const levelToTrain = availableLevels.length
      ? availableLevels[~~(Math.random() * availableLevels.length)]
      : utils.sort(utils.shuffle(levels.slice(-2)), (a, b) => {
          const aScore = getLevelProgress(a).score;
          const bScore = getLevelProgress(b).score;
          return Math.abs(aScore - bScore) >= 10 ? aScore - bScore : 0;
        })[0];

    // console.log({ levelToTrain });

    return levelToTrain.level;
  }

  /**
   * Returns the octave of a note so that it fits in between C2 and C6 inclusive (for staffID questions).
   *
   * @param {Note} note
   * @returns {number}
   */
  static staffRangeCorrect(note) {
    const newNote = note.clone();
    const c2 = new Note('C2');
    const c6 = new Note('C6');
    while (newNote.isGreaterThanIgnoreAccidental(c6) || newNote.isLessThanIgnoreAccidental(c2)) {
      newNote.octave = Math.floor(Math.random() * 5) + 2;
    }
    return newNote.octave;
  }

  levels: SolfegeLevel[] = [
    {
      level: -4,
      degrees: ['1', '2', '3', '4', '5', '6', '7', '8'],
      quality: 'Major',
      mode: 'Major',
      multiNoteSkill: 'sd_frag_stepwise_major',
      writingSolfegeSkill: 'writing_solfege_major',
      singleNoteSkill: null,
      octaveDisplacement: false,
      firstNoteGiven: true,
      multiOnly: true,
      maxInterval: 2,
    },
    {
      level: 0,
      degrees: ['1', '2', '3'],
      quality: 'Major',
      mode: 'Major',
      singleNoteSkill: 'sd_123_major',
      multiNoteSkill: 'sd_frag_123_major',
      writingSolfegeSkill: 'writing_solfege_major',
      octaveDisplacement: false,
    },
    {
      level: 1,
      degrees: ['1', '2', '3'],
      quality: 'minor',
      mode: 'naturalMinor',
      singleNoteSkill: 'sd_123_minor',
      multiNoteSkill: 'sd_frag_123_minor',
      writingSolfegeSkill: 'writing_solfege_natural_minor',
      octaveDisplacement: false,
    },
    {
      level: 2,
      degrees: ['1', '2', '3', '4', '5'],
      quality: 'Major',
      mode: 'Major',
      singleNoteSkill: 'sd_12345_major',
      multiNoteSkill: 'sd_frag_12345_major',
      writingSolfegeSkill: 'writing_solfege_major',
      octaveDisplacement: false,
    },
    {
      level: 3,
      degrees: ['1', '2', '3', '4', '5'],
      quality: 'minor',
      mode: 'naturalMinor',
      singleNoteSkill: 'sd_12345_minor',
      multiNoteSkill: 'sd_frag_12345_minor',
      writingSolfegeSkill: 'writing_solfege_natural_minor',
      octaveDisplacement: false,
    },
    {
      level: 4,
      degrees: ['5', '6', '7', '8'],
      quality: 'Major',
      mode: 'Major',
      singleNoteSkill: 'sd_5678_major',
      multiNoteSkill: 'sd_frag_5678_major',
      writingSolfegeSkill: 'writing_solfege_major',
      octaveDisplacement: false,
    },
    {
      level: 5,
      degrees: ['5', '6', 'M7', '8'],
      quality: 'minor',
      mode: 'harmonicMinor',
      singleNoteSkill: 'sd_5678_minor',
      multiNoteSkill: 'sd_frag_5678_minor',
      writingSolfegeSkill: 'writing_solfege_harmonic_minor',
      octaveDisplacement: false,
    },
    {
      level: 6,
      degrees: ['5', '6', 'M6', '7', 'M7', '8'],
      quality: 'minor',
      mode: 'allMinors',
      singleNoteSkill: 'sd_5678_all_minors',
      multiNoteSkill: 'sd_frag_5678_all_minors',
      writingSolfegeSkill: 'writing_solfege_all_minors',
      octaveDisplacement: false,
    },
    {
      level: 7,
      degrees: ['1', '2', '3', '4', '5', '6', '7', '8'],
      quality: 'Major',
      mode: 'Major',
      singleNoteSkill: 'sd_1_to_8_major',
      multiNoteSkill: 'sd_frag_1_to_8_major',
      writingSolfegeSkill: 'writing_solfege_major',
      octaveDisplacement: false,
    },
    {
      level: 8,
      degrees: ['1', '2', '3', '4', '5', '6', 'M6', '7', 'M7', '8'],
      quality: 'minor',
      mode: 'allMinors',
      singleNoteSkill: 'sd_1_to_8_minor',
      multiNoteSkill: 'sd_frag_1_to_8_minor',
      writingSolfegeSkill: 'writing_solfege_all_minors',
      octaveDisplacement: false,
    },
    {
      level: 9,
      degrees: ['1', '2', '3', '4', '5', '6', '7', '8'],
      quality: 'Major',
      mode: 'Major',
      octaveDisplacement: true,
      singleNoteSkill: 'sd_1_to_8_major_w_octaves',
      multiNoteSkill: 'sd_frag_1_to_8_major_w_octaves',
      writingSolfegeSkill: 'writing_solfege_major',
    },
    {
      level: 10,
      degrees: ['1', '2', '3', '4', '5', '6', 'M6', '7', 'M7', '8'],
      quality: 'minor',
      mode: 'allMinors',
      octaveDisplacement: true,
      singleNoteSkill: 'sd_1_to_8_minor_w_octaves',
      multiNoteSkill: 'sd_frag_1_to_8_minor_w_octaves',
      writingSolfegeSkill: 'writing_solfege_all_minors',
    },
  ];
}

function generateFragmentFromDegreesAndLargestInterval(
  possibleDegrees: ScaleDegree[],
  largestInterval: number,
  keySignature: KeySignature,
  length: number = 4,
  staff: boolean = false,
  priorNotes: { degree: ScaleDegree; note: Note }[] = [],
): { degree: ScaleDegree; note: Note }[] {
  // If no prior note, push the prior note at random:
  if (!priorNotes.length) {
    const degree = possibleDegrees[~~(Math.random() * possibleDegrees.length)];
    const note = keySignature.getNoteAtScaleDegreeOrThrow(degree);
    priorNotes.push({ degree, note });
  }

  // Grab the prior note:
  const prior = priorNotes.slice(-1)[0];

  // Get the in-range notes
  const possibleNotes: { degree: ScaleDegree; note: Note }[] = new Array(largestInterval * 2 + 1)
    // Create an array of all possible notes length
    .fill(null, 0, largestInterval * 2 + 1)
    // Fill it with generic interval numbers
    .map((_, i) => i - largestInterval)
    // We only want one unison, and 0 doesn't exist as a generic interval name, so we filter
    .filter((interval) => interval !== 0 && interval !== -1)
    // Map it to all the possible notes
    .map((interval): { degree: ScaleDegree; note: Note }[] => {
      const note = prior.note.getNoteAtGeneric(interval, keySignature);
      const degree = keySignature.getScaleDegreeAtNote(note);
      const raisedNote = note.getNoteAt('A1');
      if (keySignature.quality === 'minor' && (degree === '6' || degree === '7') && raisedNote) {
        return [
          { note, degree },
          { note: raisedNote, degree: `M${degree}` },
        ];
      }
      return [
        {
          note,
          degree: degree as ScaleDegree,
        },
      ];
    })
    .flat()
    // Now filter any not-matching scale degrees
    .filter(({ degree }) => possibleDegrees.includes(degree))
    // Avoid three of the same note in a row
    .filter(
      ({ degree }) =>
        priorNotes.length < 2 || !priorNotes.slice(-2).every((n) => n.degree === degree),
    )
    // And make sure all notes can share the same clef if needed.
    .filter(
      ({ note }) =>
        priorNotes
          .map((n) => n.note)
          .concat(note)
          .map((n) => n.prefersClef())
          .reduce((accum, noteClefs) => noteClefs.filter((clef) => accum.includes(clef))).length,
    );

  const next = possibleNotes[~~(Math.random() * possibleNotes.length)];
  priorNotes.push(next);

  if (priorNotes.length === length) return priorNotes;
  return generateFragmentFromDegreesAndLargestInterval(
    possibleDegrees,
    largestInterval,
    keySignature,
    length,
    staff,
    priorNotes,
  );
}
